// Rule parameters from evaluation-rules.yaml in this package (ruleVersions.current | legacy).
// Zod validates each doc bundle under APT_P1, SB_P1, P2, P3, AOPosture, SBPosture.

import { z } from 'zod';
import { logger } from '@/lib/logging/logger';
import { getRuleVersionDocBundle, invalidateEvaluationRulesCache } from './evaluation-rules-load';

const GaugeColorsSchema = z.object({
  level0: z.string().optional(),
  level1: z.string().optional(),
  level2: z.string().optional(),
  level3: z.string().optional(),
}).passthrough();

const SeverityBandsSchema = z.record(z.array(z.number()).length(3));

const PostureParamsSchema = z.object({
  // Legacy + sex-specific pelvic thresholds
  pelvicTiltDegMin: z.number().optional(),
  pelvicTiltDegMinMale: z.number().optional(),
  pelvicTiltDegMinFemale: z.number().optional(),

  // Sagittal/frontal posture thresholds
  hipForwardCmMin: z.number().optional(),
  trunkLeanLatDegMin: z.number().optional(),
  kneeHyperextensionDegMin: z.number().optional(),
  kneeFlexionDegMin: z.number().optional(),
  ribcageBehindPelvis: z.boolean().optional(),

  // Lateralization/posture-analysis support thresholds
  latAngleThresholdDeg: z.number().optional(),
  latShiftThresholdCm: z.number().optional(),
  cvAngleFhpThresholdDeg: z.number().optional(),

  // Optional posture-analysis tuning blocks
  gaugeColors: GaugeColorsSchema.optional(),
  severityBands: SeverityBandsSchema.optional(),
  idealValues: z.record(z.any()).optional(),
  massDistribution: z.record(z.number()).optional(),
  pelvicShiftPercentages: z.tuple([z.number(), z.number(), z.number()]).optional(),
}).passthrough();

const SopPhaseGoalsSchema = z.object({
  cervicalRotationMinDeg: z.number().optional(),
  cervicalLatFlexMinDeg: z.number().optional(),
  shoulderIRMinDeg: z.number().optional(),
  shoulderAbdMinDeg: z.number().optional(),
  pelvisRotationMinDeg: z.number().optional(),
  hipAbductionMinDeg: z.number().optional(),
  slrMinDeg: z.number().optional(),
  hipExtensionMinDeg: z.number().optional(),
  groundingPerformanceMinScore: z.number().optional(),
});

const OrientationThresholdsSchema = z.object({
  shoulderIrAptThresholdDeg: z.number().optional(),
}).passthrough();

const RuleParamsSchema = z.object({
  posture: PostureParamsSchema.optional(),
  sopPhaseGoals: SopPhaseGoalsSchema.optional(),
  orientationThresholds: OrientationThresholdsSchema.optional(),
}).passthrough();

export type PostureParams = z.infer<typeof PostureParamsSchema>;
export type SopPhaseGoals = z.infer<typeof SopPhaseGoalsSchema>;
export type OrientationThresholds = z.infer<typeof OrientationThresholdsSchema>;
export type RuleParams = z.infer<typeof RuleParamsSchema>;
export type SopPhaseDocKey = 'APT_P1' | 'SB_P1' | 'P2' | 'P3';
export type RuleDocVersion = 'current' | 'legacy';

let cachedParamsByVersion: Record<RuleDocVersion, Record<string, RuleParams> | null> = {
  current: null,
  legacy: null,
};

export function invalidateParamsCache(version?: RuleDocVersion): void {
  invalidateEvaluationRulesCache();
  if (version) {
    cachedParamsByVersion[version] = null;
    return;
  }
  cachedParamsByVersion = {
    current: null,
    legacy: null,
  };
}

function parseRuleParamsDoc(raw: unknown, key: string): RuleParams | null {
  if (!raw || typeof raw !== 'object') return null;
  try {
    return RuleParamsSchema.parse(raw);
  } catch {
    logger.warn(`[rule-params] Invalid rule bundle for ${key} in evaluation-rules.yaml`);
    return null;
  }
}

/** Kept for backwards compatibility; localStorage RuleDoc overrides were removed (rules are YAML-only). */
export function isRuleDocOverridden(_key: string): boolean {
  return false;
}

export async function loadAllRuleParams(version: RuleDocVersion = 'current'): Promise<Record<string, RuleParams>> {
  const cachedParams = cachedParamsByVersion[version];
  if (cachedParams) return cachedParams;

  const bundle = getRuleVersionDocBundle(version);
  const result: Record<string, RuleParams> = {};
  for (const key of Object.keys(bundle)) {
    const params = parseRuleParamsDoc(bundle[key], key);
    if (params) result[key] = params;
  }
  cachedParamsByVersion[version] = result;
  return result;
}

export async function getPostureParams(version: RuleDocVersion = 'current'): Promise<PostureParams> {
  const all = await loadAllRuleParams(version);
  // Prefer specific posture docs; fall back to phase docs if present
  return {
    ...all['AOPosture']?.posture,
    ...all['SBPosture']?.posture,
    ...all['APT_P1']?.posture,
    ...all['SB_P1']?.posture,
  };
}

export async function getSopPhaseGoals(phase: 'P1' | 'P2' | 'P3', version: RuleDocVersion = 'current'): Promise<SopPhaseGoals> {
  const all = await loadAllRuleParams(version);
  if (phase === 'P2') return all['P2']?.sopPhaseGoals || {};
  if (phase === 'P3') return all['P3']?.sopPhaseGoals || {};
  // P1 goals: merge from APT_P1 and SB_P1 when defined
  return {
    ...all['APT_P1']?.sopPhaseGoals,
    ...all['SB_P1']?.sopPhaseGoals
  };
}

export async function getOrientationThresholds(version: RuleDocVersion = 'current'): Promise<OrientationThresholds> {
  const all = await loadAllRuleParams(version);
  return {
    ...all['APT_P1']?.orientationThresholds,
    ...all['SB_P1']?.orientationThresholds,
  };
}

const compareOptional = (
  label: string,
  left: number | undefined,
  right: number | undefined,
  comparator: 'lte' | 'gte',
  issues: string[]
): void => {
  if (left === undefined || right === undefined) return;
  if (comparator === 'lte' && left > right) {
    issues.push(`${label}: expected ${left} <= ${right}.`);
  }
  if (comparator === 'gte' && left < right) {
    issues.push(`${label}: expected ${left} >= ${right}.`);
  }
};

export function getSopPhaseGoalCoherenceIssues(
  paramsByDoc: Partial<Record<SopPhaseDocKey, RuleParams | null | undefined>>
): string[] {
  const issues: string[] = [];

  const aptP1 = paramsByDoc.APT_P1?.sopPhaseGoals ?? {};
  const sbP1 = paramsByDoc.SB_P1?.sopPhaseGoals ?? {};
  const p2 = paramsByDoc.P2?.sopPhaseGoals ?? {};
  const p3 = paramsByDoc.P3?.sopPhaseGoals ?? {};

  if (aptP1.shoulderIRMinDeg !== undefined && sbP1.shoulderIRMinDeg !== undefined && aptP1.shoulderIRMinDeg !== sbP1.shoulderIRMinDeg) {
    issues.push(
      `P1 shoulder IR minimum mismatch: APT (${aptP1.shoulderIRMinDeg}) vs SB (${sbP1.shoulderIRMinDeg}).`
    );
  }
  if (aptP1.slrMinDeg !== undefined && sbP1.slrMinDeg !== undefined && aptP1.slrMinDeg !== sbP1.slrMinDeg) {
    issues.push(`P1 SLR minimum mismatch: APT (${aptP1.slrMinDeg}) vs SB (${sbP1.slrMinDeg}).`);
  }
  if (aptP1.hipExtensionMinDeg !== undefined && sbP1.hipExtensionMinDeg !== undefined && aptP1.hipExtensionMinDeg !== sbP1.hipExtensionMinDeg) {
    issues.push(`P1 hip extension minimum mismatch: APT (${aptP1.hipExtensionMinDeg}) vs SB (${sbP1.hipExtensionMinDeg}).`);
  }

  compareOptional(
    'Shoulder IR progression (P1->P2)',
    aptP1.shoulderIRMinDeg,
    p2.shoulderIRMinDeg,
    'lte',
    issues
  );
  compareOptional(
    'Shoulder Abduction progression (P2->P3)',
    p2.shoulderAbdMinDeg,
    p3.shoulderAbdMinDeg,
    'lte',
    issues
  );
  compareOptional(
    'Pelvis Rotation progression (P2->P3)',
    p2.pelvisRotationMinDeg,
    p3.pelvisRotationMinDeg,
    'lte',
    issues
  );
  compareOptional(
    'Hip Abduction progression (P2->P3)',
    p2.hipAbductionMinDeg,
    p3.hipAbductionMinDeg,
    'lte',
    issues
  );

  if (p2.hipExtensionMinDeg !== undefined && aptP1.hipExtensionMinDeg !== undefined && p2.hipExtensionMinDeg !== aptP1.hipExtensionMinDeg) {
    issues.push(`Hip extension minimum consistency mismatch between P1 (${aptP1.hipExtensionMinDeg}) and P2 (${p2.hipExtensionMinDeg}).`);
  }

  if (p3.groundingPerformanceMinScore === undefined) {
    issues.push('P3 groundingPerformanceMinScore is missing.');
  }

  return issues;
}
